import { ActionContext, ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { CaseFeedbackState } from "@/CaseFeedback/store/CaseFeedbackState";
import caseFeedbackService from "@/CaseFeedback/services/CaseFeedbackService";
import { hospitalSupportsCaseFeedback } from "@/CaseFeedback/store/featureResolver";

const getDefaultFeedbackState: () => CaseFeedbackState = () => ({
  rating: 0,
  notes: "",
  isLoading: false,
  didFailToSendFeedback: false,
  alertMessage: undefined,
  isVisible: false,
  isSubmitButtonEnabled: false,
  pendingFeedbackForCaseId: undefined,
  shouldNotDisturb: true,
});

export interface CaseFeedbackGetters extends GetterTree<CaseFeedbackState, unknown> {
  notes: (state: CaseFeedbackState) => string;
  rating: (state: CaseFeedbackState) => number;
  isLoading: (state: CaseFeedbackState) => boolean;
  isSubmitButtonEnabled: (state: CaseFeedbackState) => boolean;
  alertMessage: (state: CaseFeedbackState) => string | undefined;
  shouldNotDisturb: (state: CaseFeedbackState) => boolean;
  shouldRequestFeedback: (state: CaseFeedbackState) => boolean;
}

export interface CaseFeedbackActions extends ActionTree<CaseFeedbackState, unknown> {
  onViewLoaded(context: ActionContext<CaseFeedbackState, unknown>);
  setPendingFeedback(context: ActionContext<CaseFeedbackState, unknown>, caseId: string | undefined);
  submitFeedback(context: ActionContext<CaseFeedbackState, unknown>);
  dismissFeedback(context: ActionContext<CaseFeedbackState, unknown>);
  acknowledgeAlert(context: ActionContext<CaseFeedbackState, unknown>);
  doNotDisturbClicked(context: ActionContext<CaseFeedbackState, unknown>);
  updateNotes(context: ActionContext<CaseFeedbackState, unknown>, notes: string);
  updateRating(context: ActionContext<CaseFeedbackState, unknown>, rating: number);
}

interface CaseFeedbackMutations extends MutationTree<CaseFeedbackState> {
  setPendingFeedback(state: CaseFeedbackState, caseId: string);
  requestFeedback(state: CaseFeedbackState);
  setLoading(state: CaseFeedbackState, isLoading: boolean);
  setDidFailToSendFeedback(state: CaseFeedbackState, didFail: boolean);
  setAlertMessage(state: CaseFeedbackState, message: string | undefined);
  setDoNotDisturb(state: CaseFeedbackState, shouldNotDisturb: boolean);
  resetState(state: CaseFeedbackState);
  setRating(state: CaseFeedbackState, rating: number);
  setNotes(state: CaseFeedbackState, notes: string);
}

interface CaseFeedbackStore extends Module<CaseFeedbackState, unknown> {
  state: CaseFeedbackState;
  actions: CaseFeedbackActions;
  mutations: CaseFeedbackMutations;
  getters: CaseFeedbackGetters;
}

const getters: CaseFeedbackGetters = {
  alertMessage: (state: CaseFeedbackState) => state.alertMessage,
  notes: (state: CaseFeedbackState) => state.notes,
  rating: (state: CaseFeedbackState) => state.rating,
  isLoading: (state: CaseFeedbackState) => state.isLoading,
  isSubmitButtonEnabled: (state: CaseFeedbackState) => state.isSubmitButtonEnabled,
  shouldNotDisturb: (state: CaseFeedbackState) => state.shouldNotDisturb,
  shouldRequestFeedback: (state: CaseFeedbackState) => {
    return state.alertMessage != undefined || state.pendingFeedbackForCaseId != undefined;
  },
};

export const actions: CaseFeedbackActions = {
  onViewLoaded(context: ActionContext<CaseFeedbackState, unknown>) {
    const savedDoNotDisturb = caseFeedbackService.readDoNotDisturbPreference(context.rootGetters["Login/userId"]);
    context.commit("setDoNotDisturb", savedDoNotDisturb);
  },
  setPendingFeedback(context: ActionContext<CaseFeedbackState, unknown>, caseId: string | undefined) {
    const shouldNotDisturb = caseFeedbackService.readDoNotDisturbPreference(context.rootGetters["Login/userId"]);
    const supportsCaseFeedback = hospitalSupportsCaseFeedback(context.rootGetters["Case/destinationHospital"]);
    if (caseId && !shouldNotDisturb && supportsCaseFeedback) {
      context.commit("setPendingFeedback", caseId);
    }
  },
  async submitFeedback(context: ActionContext<CaseFeedbackState, unknown>) {
    const { rating, notes, pendingFeedbackForCaseId, isLoading } = context.state;
    if (pendingFeedbackForCaseId && !isLoading) {
      context.commit("setLoading", true);
      try {
        await caseFeedbackService.sendFeedback(pendingFeedbackForCaseId, rating, notes);
        context.commit("setAlertMessage", "Your feedback has been submitted to a hospital representative, thank you.");
      } catch (error: any) {
        if (error.response?.data?.message) {
          context.commit("setAlertMessage", error.response.data.message);
        } else {
          context.commit("setAlertMessage", "Feedback submission failed. Would you like to try again?");
        }
        context.commit("setDidFailToSendFeedback", true);
      } finally {
        context.commit("setLoading", false);
      }
    }
  },
  dismissFeedback(context: ActionContext<CaseFeedbackState, unknown>) {
    if (context.state.didFailToSendFeedback) {
      context.commit("setDidFailToSendFeedback", false);
      context.commit("setAlertMessage", undefined);
    } else {
      context.commit("resetState");
    }
  },
  acknowledgeAlert(context: ActionContext<CaseFeedbackState, unknown>) {
    context.dispatch("dismissFeedback");
  },
  doNotDisturbClicked(context: ActionContext<CaseFeedbackState, unknown>) {
    const shouldNotDisturb = !context.state.shouldNotDisturb;
    context.commit("setDoNotDisturb", shouldNotDisturb);
    caseFeedbackService.saveDoNotDisturbPreference(context.rootGetters["Login/userId"], shouldNotDisturb);
  },
  updateNotes(context: ActionContext<CaseFeedbackState, unknown>, notes: string) {
    context.commit("setNotes", notes);
  },
  updateRating(context: ActionContext<CaseFeedbackState, unknown>, rating: number) {
    context.commit("setRating", rating);
  },
};

const mutations: CaseFeedbackMutations = {
  setPendingFeedback(state: CaseFeedbackState, caseId: string) {
    state.pendingFeedbackForCaseId = caseId;
  },
  requestFeedback(state: CaseFeedbackState) {
    state.isVisible = true;
  },
  setLoading(state: CaseFeedbackState, isLoading: boolean) {
    state.isLoading = isLoading;
  },
  setAlertMessage(state: CaseFeedbackState, message: string | undefined) {
    state.alertMessage = message;
  },
  setDidFailToSendFeedback(state: CaseFeedbackState, didFail: boolean) {
    state.didFailToSendFeedback = didFail;
  },
  setDoNotDisturb(state: CaseFeedbackState, shouldNotDisturb: boolean) {
    state.shouldNotDisturb = shouldNotDisturb;
  },
  resetState(state: CaseFeedbackState) {
    const initialState = getDefaultFeedbackState();
    Object.keys(initialState).forEach((key) => {
      state[key] = initialState[key];
    });
  },
  setNotes(state: CaseFeedbackState, notes: string) {
    state.notes = notes;
  },
  setRating(state: CaseFeedbackState, rating: number) {
    state.rating = rating;
    state.isSubmitButtonEnabled = rating != 0;
  },
};

const store: CaseFeedbackStore = {
  namespaced: true,
  state: getDefaultFeedbackState(),
  getters,
  actions,
  mutations,
};

export default store;
